import React, { Fragment, Ref } from 'react';
import styled from '@emotion/styled';
import TextField, { TextFieldProps } from '@atlaskit/textfield';
import { Field, ErrorMessage } from '@atlaskit/form';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export interface FormTextFieldProps extends Omit<TextFieldProps, 'label'> {
  name: string;
  label: React.ReactNode;
  isLabelHidden?: boolean;
  invalidMessage?: React.ReactNode;
  testId?: string;
  elemAfterInputExternal?: React.ReactNode;
}

// override the text field border to avoid nested borders when using elemAfterInputExternal
const BorderOverride = styled.div`
  border: 1px solid ${token('color.border.input')};
  border-radius: 4px;

  * {
    border: none !important; // important styles needed for Safari
  }
`;

// required because currently the Atlaskit elemAfterInput does not support nesting focus correctly.
// once https://product-fabric.atlassian.net/browse/DSP-14006 has been resolved, we should be able to remove this code.
const MaybeCustomBorder = ({
  useCustomBordering,
  children,
}: {
  useCustomBordering: boolean;
  children: React.ReactNode;
}) => {
  if (useCustomBordering) {
    return <BorderOverride>{children}</BorderOverride>;
  }
  return <>{children}</>;
};

const FormTextField = (
  {
    name,
    label,
    isLabelHidden,
    invalidMessage,
    testId,
    isRequired,
    elemAfterInputExternal,
    ...props
  }: FormTextFieldProps,
  ref: Ref<HTMLInputElement>
) => {
  return (
    <Field
      name={name}
      label={!isLabelHidden && label}
      isRequired={isRequired}
      aria-required={isRequired}>
      {({ fieldProps }) => (
        <Fragment>
          <MaybeCustomBorder useCustomBordering={!!elemAfterInputExternal}>
            <Inline alignBlock="center">
              <TextField {...fieldProps} {...props} testId={testId} ref={ref} />
              {elemAfterInputExternal}
            </Inline>
          </MaybeCustomBorder>
          {invalidMessage && <ErrorMessage>{invalidMessage}</ErrorMessage>}
        </Fragment>
      )}
    </Field>
  );
};

export default React.forwardRef<HTMLInputElement, FormTextFieldProps>(FormTextField);
